"use client";

import { useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Dialog, Modal } from "staysure-component-library";

import PaymentModalContent from "@/components/molecules/payment-section/payment-modal-content/payment-modal-content";
import useGtmPaymentFaildOptions from "@/hooks/gtm/payment/use-gtm-payment-failed-options";
import { useRenewalPaymentSummaryStateContext } from "@/providers/renewal-payment-summary-auto-pay-state-provider";
import { PaymentFailedModalSchemaType } from "@/schema/renewal-page/payment-summary-page.schema";
import { RootState } from "@/store/root-reducer";
import paymentFailedDataMap from "@/utils/gtm/payment-failed-data-map";

type PaymentModalProps = {
  content: PaymentFailedModalSchemaType;
  tryAgainSubmit?: () => void;
};

export function PaymentFailedModal({
  content,
  tryAgainSubmit,
}: Readonly<PaymentModalProps>) {
  const router = useRouter();

  const { referenceId, attributes } = useSelector(
    (state: RootState) => state.quote
  );
  const { paymentDetails } = attributes;

  const [isOpen, setIsOpen] = useState(false);
  const searchParams = useSearchParams();
  const paymentError = searchParams?.get("paymentError");
  const pclError = searchParams?.get("pclError");
  const pclCancel = searchParams?.get("pclCancel");
  const mtaSuccessRenewalFailed = searchParams?.get("mtaSuccessRenewalFailed");

  const { setAutoPaySelected, setIsOpenCardholderConsentModal } =
    useRenewalPaymentSummaryStateContext();

  const isCheck = !!(
    ((paymentError && paymentError.trim() !== "") ||
      (pclCancel && pclCancel.trim() !== "") ||
      (mtaSuccessRenewalFailed && mtaSuccessRenewalFailed.trim() !== "") ||
      (pclError && pclError.trim() !== "")) &&
    referenceId
  );

  useEffect(() => {
    if (paymentError || pclCancel || pclError || mtaSuccessRenewalFailed) {
      setIsOpen(true);
    }
  }, [paymentError, pclCancel, pclError, mtaSuccessRenewalFailed]);

  const errorType = useMemo(() => {
    if (paymentError) return "paymentError";
    if (pclError) return "pclError";
    if (pclCancel) return "pclCancel";
    if (mtaSuccessRenewalFailed) return "mtaSuccessRenewalFailed";
    return "paymentError";
  }, [paymentError, mtaSuccessRenewalFailed, pclCancel, pclError]);

  const modalContent = useMemo(() => {
    const errorContent = {
      paymentError: paymentDetails?.autoPaySelected
        ? content.genericPaymentFiledExistingCardModalContent
        : content.genericPaymentFiledModalContent,
      pclError: content.pclPaymentFailedModalContent,
      pclCancel: content.pclPaymentCancelModalContent,
      mtaSuccessRenewalFailed: content.mtaPaymentFailedModalContent,
    };

    return errorContent[errorType] ?? content.genericPaymentFiledModalContent;
  }, [content, errorType, paymentDetails?.autoPaySelected]);

  const clearRoute = (status: boolean) => {
    setIsOpen(status);
    const { pathname } = window.location;
    const urlWithoutParams = window.location.origin + pathname;
    router.replace(urlWithoutParams);
  };

  // GTM Trigger
  let gtmPaymentFailed = null;
  if (referenceId) {
    gtmPaymentFailed = paymentFailedDataMap(
      attributes,
      modalContent.title ?? "",
      !!attributes,
      "site_errors",
      "payment_failed",
      "renewal_payment_error",
      "renewal"
    );
  }

  useGtmPaymentFaildOptions({
    paymentFaildData: gtmPaymentFailed,
    isReady: isCheck,
  });

  const primaryButtonAction = () => {
    clearRoute(false);
    if (
      paymentDetails?.autoPaySelected &&
      tryAgainSubmit &&
      !pclError &&
      !pclCancel
    ) {
      tryAgainSubmit();
    }
  };

  return (
    <Modal
      isDismissable={false}
      onOpenChange={(e: boolean) => setIsOpen(e)}
      isOpen={isOpen}
      modalStyles={{
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      }}
      className="bg-surface-neutral-option-1 w-[312px] md:w-[442px] lg:w-[588px] items-center justify-center  relative rounded-radius-xxl"
      uderlayClassName="z-[200] bg-interactive-overlay-default"
    >
      <Dialog>
        <PaymentModalContent
          ModalTitle={modalContent.title}
          ModalContent={modalContent.description}
          ModalButton={modalContent.ctaLabel}
          secondaryButtonLabel={modalContent?.secondaryCtaLabel ?? ""}
          setIsOpen={primaryButtonAction}
          isEnableSecondaryButton={paymentDetails?.autoPaySelected}
          secondaryButtonAction={() => {
            setAutoPaySelected(false);
            setIsOpenCardholderConsentModal(true);
            clearRoute(false);
          }}
        />
      </Dialog>
    </Modal>
  );
}
